import { Briefcase, Crown, Megaphone, Wine } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { renderBoldText, sectionCls } from '@/pages/launch/helpers';

interface LaunchProblemSectionProps {
  sectionRef: React.Ref<HTMLDivElement>;
  visible: boolean;
}

const problemCards = [
  {
    icon: Briefcase,
    name: 'The GM, Monday Morning',
    quote: "How'd we actually do Saturday?",
    pain: "Opens Eventbrite. Checks the POS. Pulls up last week's spreadsheet. Texts the promoter. <b>45 minutes later, the answer is still a guess.</b>",
    data: 'Venue ops teams burn <b>12\u201318 hours/week</b> on manual data work.\u00B2 Only <b>24%</b> of venues have their core systems talking to each other.\u00B3',
    color: 'hsl(var(--primary))',
  },
  {
    icon: Wine,
    name: 'The Bar Manager, 11pm',
    quote: 'Are we overstaffed or understaffed?',
    pain: 'No walk-up projection. No revenue-per-head benchmark. He scheduled 6 bartenders because "last Saturday was busy." <b>Tonight it\'s slow.</b>',
    data: '<b>$31,200/year</b> in scheduling waste at a single bar.\u2074 <b>20% average shrinkage</b> across 5,000+ audits.\u2075',
    color: 'hsl(var(--warning))',
  },
  {
    icon: Crown,
    name: 'The VIP Host, Midnight',
    quote: 'Table 4 just asked for a bottle menu.',
    pain: 'That guest spent $4K on bottle service three months ago. Bought a GA ticket tonight. <b>Nobody flagged it.</b> No upgrade offer, no VIP treatment. They leave feeling like a stranger.',
    data: "<b>77.4%</b> of guests never come back. The ones who do? <b>Worth 26x more.</b>\u2076 Olo's data from 100M records: <b>60% of revenue</b> comes from repeat guests.\u2077",
    color: 'hsl(var(--success))',
  },
  {
    icon: Megaphone,
    name: 'The Marketing Lead, 3 Days Out',
    quote: 'Ticket sales are soft. What should I run?',
    pain: 'She needs the sales curve, last show\'s conversion rate, which audience segment to target. That\'s 4 systems. So she texts the team "push something out" and boosts a post for $200 with no targeting.',
    data: "<b>40\u201360%</b> of digital ad spend is wasted on bad placement and bots.\u2078 Venues' #1 challenge above rent and staffing: <b>marketing.</b>\u00B9",
    color: 'hsl(var(--accent))',
  },
] as const;

const LaunchProblemSection = ({ sectionRef, visible }: LaunchProblemSectionProps) => (
  <>
    <section
      id="problem"
      ref={sectionRef}
      className={`py-12 sm:py-20 px-4 sm:px-6 bg-muted/30 ${sectionCls(visible)}`}
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <Badge variant="outline" className="mb-4">
            We Know Your Saturday Night
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-display font-bold">
            This Happens Every Weekend.
            <br className="hidden sm:block" />{' '}
            <span className="gradient-text">And It's Costing You.</span>
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            These aren't hypotheticals. They're the conversations happening in every venue, every
            week.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {problemCards.map((p) => (
            <Card
              key={p.name}
              className="p-6 hover:shadow-lg transition-all border-l-4"
              style={{ borderLeftColor: p.color }}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="w-10 h-10 rounded-full flex items-center justify-center bg-muted">
                  <p.icon size={18} className="text-muted-foreground" />
                </span>
                <div>
                  <p className="font-semibold text-sm">{p.name}</p>
                  <p className="text-xs italic text-muted-foreground">"{p.quote}"</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                {renderBoldText(p.pain)}
              </p>
              <div className="rounded-lg bg-muted/50 border border-border/40 px-3 py-2.5">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {renderBoldText(p.data)}
                </p>
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-8 pt-6 border-t border-border/40">
          <p className="text-[10px] text-muted-foreground/60 leading-relaxed">
            &sup1; NIVA State of Live / TEConomy Partners, June 2025 &nbsp;&sup2; SynergySuite, 2026
            &nbsp;&sup3; Starfleet Research, 2025 &nbsp;&sup4; 7shifts (Bar 404 case study)
            &nbsp;&sup5; BevInco, 20-year study of 1,000+ bars &nbsp;&sup6; Bloom Intelligence, 2026
            &nbsp;&sup7; Olo, 100M guest records, 2024 &nbsp;&sup8; Proxima Group
          </p>
        </div>
      </div>
    </section>

    <div className="py-10 sm:py-14 px-4 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-y border-primary/15">
      <p className="text-base sm:text-lg text-center max-w-3xl mx-auto leading-relaxed">
        <span className="font-bold text-primary text-xl sm:text-2xl">76%</span>{' '}
        <span className="text-foreground font-medium">
          of operators say tech gives them an edge.
        </span>{' '}
        Only <span className="font-bold text-primary text-xl sm:text-2xl">13%</span>{' '}
        <span className="text-foreground font-medium">are happy with their tools.</span>
        <span className="text-muted-foreground"> We built VenueIntel for that gap.</span>
      </p>
    </div>
  </>
);

export default LaunchProblemSection;
